import React, { useState, useEffect } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
} from '@mui/material';
import {
    createAuthority,
    editActivity,
    get_states,
    get_districts_list,
} from '../../helper/Api';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomBreadcrumbs from '../Tools/Breadcumbs';
import Spinner from '../Tools/spinner';
import AddAuthorityPreview from '../Modal/AddAuthorityPreview';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;
const PINCODE_REGEX = /^\d{6}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/i;
const ACCOUNT_NO_REGEX = /^\d{9,18}$/;

const AddAuthority = () => {
    const { id } = useParams();
    const [showPreview, setShowPreview] = useState(false);
    const [loading, setLoading] = useState(false);
    const { authData } = useSelector((state) => state.authData);
    const [states_list, setStates_list] = useState([]);
    const [district_list, setDistrict_list] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobile: '',
        district: '',
        state: '',
        pincode: '',
        address: '',
        bank_name: '',
        branch: '',
        account_no: '',
        ifsc_code: '',
        created_by: authData.user.id,
    });
    const navigate = useNavigate();

    const getStateName = (stateId) => {
        const state = states_list.find((item) => String(item.id) === String(stateId));
        return state?.name || stateId;
    };

    const getDistrictName = (districtId) => {
        const district = district_list.find((item) => String(item.id) === String(districtId));
        return district?.city || districtId;
    };

    const handleFormData = (e) => {
        const { name, value } = e.target;

        if (name === 'state') {
            setFormData((prev) => ({
                ...prev,
                state: value,
                district: '',
            }));
            return;
        }

        if (name === 'ifsc_code') {
            setFormData((prev) => ({
                ...prev,
                ifsc_code: value.toUpperCase(),
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const errors = [];

        if (!String(formData.name || '').trim()) {
            errors.push('Name is required.');
        }
        if (!String(formData.email || '').trim()) {
            errors.push('Email is required.');
        } else if (!EMAIL_REGEX.test(formData.email.trim())) {
            errors.push('Please enter a valid email address.');
        }
        if (!String(formData.mobile || '').trim()) {
            errors.push('Mobile is required.');
        } else if (!MOBILE_REGEX.test(String(formData.mobile).trim())) {
            errors.push('Please enter a valid 10-digit mobile number.');
        }
        if (!formData.state) {
            errors.push('State is required.');
        }
        if (!formData.district) {
            errors.push('District is required.');
        }
        if (!String(formData.pincode || '').trim()) {
            errors.push('Pincode is required.');
        } else if (!PINCODE_REGEX.test(String(formData.pincode).trim())) {
            errors.push('Please enter a valid 6-digit pincode.');
        }
        if (!String(formData.address || '').trim()) {
            errors.push('Address is required.');
        }
        if (!String(formData.bank_name || '').trim()) {
            errors.push('Bank Name is required.');
        }
        if (!String(formData.branch || '').trim()) {
            errors.push('Branch is required.');
        }
        if (!String(formData.account_no || '').trim()) {
            errors.push('Account No is required.');
        } else if (!ACCOUNT_NO_REGEX.test(String(formData.account_no).trim())) {
            errors.push('Please enter a valid account number (9–18 digits).');
        }
        if (!String(formData.ifsc_code || '').trim()) {
            errors.push('IFSC Code is required.');
        } else if (!IFSC_REGEX.test(formData.ifsc_code.trim())) {
            errors.push('Please enter a valid IFSC code.');
        }

        return errors;
    };

    const previewHandler = (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (validationErrors.length) {
            toast.error(validationErrors[0], {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        setShowPreview(true);
    };

    useEffect(() => {
        get_state();
    }, []);

    useEffect(() => {
        if (formData.state) {
            get_districts();
        } else {
            setDistrict_list([]);
        }
    }, [formData.state]);

    const get_state = async () => {
        try {
            const states_response = await get_states();
            if (states_response.data.status) {
                setStates_list(states_response.data.list);
            } else {
                setStates_list([]);
            }
        } catch (error) {
            setStates_list([]);
        }
    };

    const get_districts = async () => {
        try {
            const { data } = await get_districts_list(formData.state);
            if (data.status) {
                setDistrict_list(data.list);
            } else {
                setDistrict_list([]);
            }
        } catch (error) {
            setDistrict_list([]);
        }
    };

    const handleSubmit = async () => {
        const validationErrors = validateForm();
        if (validationErrors.length) {
            toast.error(validationErrors[0], {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        try {
            setLoading(true);
            if (id) {
                const response = await editActivity(
                    { activity: formData.activity, status: formData.status },
                    id
                );
                if (response.data.status) {
                    toast.success(response.data.message, {
                        position: toast.POSITION.TOP_CENTER,
                    });
                    navigate('/department/activity');
                } else {
                    toast.error(response.data.message, {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
            } else {
                const response = await createAuthority(formData);
                if (response.data.status) {
                    toast.success(response.data.message, {
                        position: toast.POSITION.TOP_CENTER,
                    });
                    navigate('/authoritys');
                } else {
                    toast.error(response.data.message, {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message || 'Failed to save authority', {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
            setShowPreview(false);
        }
    };

    const linksArray = [
        {
            href: '/authoritys',
            label: 'Authorities',
        },
    ];

    const pageTitle = id ? 'Edit Authority' : 'Add Authority';

    return (
        <div className="p-3">
            {loading && <Spinner />}

            <AddAuthorityPreview
                showPreview={showPreview}
                setShowPreview={setShowPreview}
                formData={formData}
                handleSubmit={handleSubmit}
                stateName={getStateName(formData.state)}
                districtName={getDistrictName(formData.district)}
            />

            <div className="row mb-3">
                <div className="col-md-6">
                    <h4 style={{ color: '#28326f', margin: 0 }}>{pageTitle}</h4>
                    <CustomBreadcrumbs linksArray={linksArray} title={pageTitle} />
                </div>
                <div className="col-md-6">
                    <div className="d-flex gap-2" style={{ width: 'auto', float: 'right' }}>
                        <button
                            type="button"
                            className="submit_button"
                            onClick={previewHandler}
                            disabled={loading}
                        >
                            Submit
                        </button>
                    </div>
                </div>
            </div>

            <div className="card p-3">
                <form onSubmit={previewHandler}>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    id="name"
                                    label="Name"
                                    variant="outlined"
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleFormData}
                                    placeholder="Enter Name"
                                    required
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    id="email"
                                    label="Email"
                                    variant="outlined"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleFormData}
                                    placeholder="Enter Email"
                                    required
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    id="mobile"
                                    label="Mobile"
                                    variant="outlined"
                                    type="text"
                                    name="mobile"
                                    value={formData.mobile}
                                    onChange={handleFormData}
                                    placeholder="Enter Mobile"
                                    inputProps={{ maxLength: 10 }}
                                    required
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <InputLabel id="state-label">State</InputLabel>
                                <Select
                                    labelId="state-label"
                                    id="state"
                                    name="state"
                                    value={formData.state || ''}
                                    label="State"
                                    onChange={handleFormData}
                                    required
                                >
                                    <MenuItem value="" disabled>
                                        Select State
                                    </MenuItem>
                                    {states_list.map((state) => (
                                        <MenuItem value={state.id} key={state.id}>
                                            {state.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <InputLabel id="district-label">District</InputLabel>
                                <Select
                                    labelId="district-label"
                                    id="district"
                                    name="district"
                                    value={formData.district || ''}
                                    label="District"
                                    onChange={handleFormData}
                                    disabled={!formData.state}
                                    required
                                >
                                    <MenuItem value="" disabled>
                                        Select District
                                    </MenuItem>
                                    {district_list.map((district) => (
                                        <MenuItem value={district.id} key={district.id}>
                                            {district.city}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    id="pincode"
                                    label="Pincode"
                                    variant="outlined"
                                    type="text"
                                    name="pincode"
                                    value={formData.pincode}
                                    onChange={handleFormData}
                                    placeholder="Enter Pincode"
                                    inputProps={{ maxLength: 6 }}
                                    required
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    id="address"
                                    label="Address"
                                    variant="outlined"
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleFormData}
                                    placeholder="Enter Address"
                                    required
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    id="bank_name"
                                    label="Bank Name"
                                    variant="outlined"
                                    type="text"
                                    name="bank_name"
                                    value={formData.bank_name}
                                    onChange={handleFormData}
                                    placeholder="Enter Bank Name"
                                    required
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    id="branch"
                                    label="Branch"
                                    variant="outlined"
                                    type="text"
                                    name="branch"
                                    value={formData.branch}
                                    onChange={handleFormData}
                                    placeholder="Enter Branch"
                                    required
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    id="account_no"
                                    label="Account No"
                                    variant="outlined"
                                    type="text"
                                    name="account_no"
                                    value={formData.account_no}
                                    onChange={handleFormData}
                                    placeholder="Enter Account No"
                                    required
                                />
                            </FormControl>
                        </div>

                        <div className="col-md-6 mb-3">
                            <FormControl fullWidth>
                                <TextField
                                    id="ifsc_code"
                                    label="IFSC Code"
                                    variant="outlined"
                                    type="text"
                                    name="ifsc_code"
                                    value={formData.ifsc_code}
                                    onChange={handleFormData}
                                    placeholder="Enter IFSC Code"
                                    inputProps={{ maxLength: 11, style: { textTransform: 'uppercase' } }}
                                    required
                                />
                            </FormControl>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddAuthority;
